// sls-app-activation.js
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from "firebase/auth";
import { getQueryParam, getCookie } from './sls-lib';

// Config
const firebaseConfig = {
  apiKey: "AIzaSyCrJgWRmTDiSJ9JxtzT_Ioo4DLkpvNnUvk",
  authDomain: "nucalmv3.firebaseapp.com",
  projectId: "nucalmv3",
  appId: "1:213610832352:web:92a5fb4fe6c75718138b85"
};
const validationUrl = "https://us-central1-nucalmv3.cloudfunctions.net/shopify-https-api-validateActivationToken";

// Get inputs
let appTokenVal = getCookie('appToken');
let oid = getQueryParam("oid");
let lid = getQueryParam("lid");
let activationMethod = "TOKEN";
let errorFound = false;

// UI Elements
const ui = {
  spinner: document.getElementById('loading-animation'),
  emailActivation: document.getElementById('email-activation-screen'),
  activateStep1: document.getElementById('activate-sub-step-1'),
  activateStep2: document.getElementById('activate-sub-step-2'),
  loginError: document.getElementById('login-error'),
  generalError: document.getElementById('general-error'),
  activationError: document.getElementById('activation-error'),
  activationRetry: document.getElementById('activation-retry')
};

// Input validation
if (!appTokenVal && !(oid && lid)) {
  oid = window.sessionStorage.getItem('oid');
  lid = window.sessionStorage.getItem('lid');
}
if (oid && lid) {
  activationMethod = "ORDER_INFO";
  appTokenVal = '';
} else {
  // Check for token
  let tkn = getQueryParam("tkn");
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (tkn && uuidRegex.test(tkn)) {
    updateAppTokenCookie(tkn);
    appTokenVal = tkn;
    activationMethod = "TOKEN";
  } else if (appTokenVal) {
    activationMethod = "TOKEN";
  } else {
    console.log('Activation data is missing');
    errorFound = true;
    showActivationErrorScreen();
  }
}
console.log(`App Token: ${appTokenVal}. OID: ${oid}. LID: ${lid}`);

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const functions = getFunctions();
const activateSubscription = httpsCallable(functions, "shopify-https-api-activateSubscription");

/**
* Sets the 'appToken' cookie, overwriting any existing value.
* Uses HTTPS only, Strict SameSite, and creates a session cookie.
* @param {string} newId - The new unique ID to set in the cookie.
*/
function updateAppTokenCookie(newId) {
    const cookieName = 'appToken';
    try {
        const encodedId = encodeURIComponent(newId);
        document.cookie = `${cookieName}=${encodedId}; path=/; Secure; SameSite=Strict`;
    } catch (error) {
        console.error("Error updating cookie:", error);
        alert('There was an issue adding the item to the cart. Please try again.');
    }
}

function isEmbeddedBrowser() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;

  return (
    // Meta/Facebook embedded browsers
    ua.includes("FBAN") || ua.includes("FBAV") ||
    // Instagram embedded browser
    ua.includes("Instagram") ||
    // TikTok embedded browser
    ua.includes("TikTok") ||
    // Twitter/X embedded browser
    ua.includes("TwitterAndroid") ||
    // LinkedIn embedded browser
    ua.includes("LinkedInApp") ||
    // Snapchat embedded browser
    ua.includes("Snapchat") ||
    // Pinterest embedded browser
    ua.includes("Pinterest") ||
    // YouTube embedded browser
    ua.includes("GSA/") // Google Search App
  );
}

function isAndroidDevice() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return /android/i.test(ua);
}

function hideElementsForEmbeddedBrowsers() {
  // Hide Google login for all embedded browsers
  const googleLogin = document.getElementById('google-login');
  if (googleLogin) {
    googleLogin.style.display = 'none';
  }

  if (isAndroidDevice()) {
    // Hide Apple login for Android users
    const appleLogin = document.getElementById('apple-login');
    if (appleLogin) {
      appleLogin.style.display = 'none';
    }
    
    // Hide the OR divider for Android users (since they only have email/password)
    const loginOptionsOr = document.getElementById('login-options-or');
    if (loginOptionsOr) {
      loginOptionsOr.style.display = 'none';
    }
  }
}

function validateTokenWithRetry(appTokenVal, validationUrl, attempt = 0) {
  if (!appTokenVal || activationMethod !== "TOKEN") {
    showActivationErrorScreen();
    return;
  }

  fetch(validationUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ appToken: appTokenVal })
  })
  .then(response => {
    if (!response.ok) {
      if (response.status === 404 && attempt < 2) {
        console.warn("Token not found yet. Retrying in 3 seconds...");
        setTimeout(() => validateTokenWithRetry(appTokenVal, validationUrl, attempt + 1), 3000);
      } else {
        showActivationRetryScreen();
      }
    } else {
      showActivateSubScreen();
    }
  })
  .catch(error => {
    console.error('Error validating token:', error);
    showActivationErrorScreen();
  });
}

function activateUserAccount(user, attempts = 0) {
  if (!user) {
    console.error("User is not authenticated.");
    showActivationErrorScreen();
    return;
  }

  console.log('Activating user account for:', user);

  const data = appTokenVal ? { appToken: appTokenVal } : { oid, lid };

  activateSubscription(data)
    .then((result) => {
      console.log('Subscription Activated:', result.data);
      window.sessionStorage.setItem('activationStep', 2);
      showScreen(ui.activateStep2);
    })
    .catch((error) => {
      if (error.code === "not-found") {
        if (attempts < 1) {
          console.warn("Order not processed yet. Trying again in 3 seconds.");
          setTimeout(() => activateUserAccount(user, attempts + 1), 3000);
        } else {
          console.warn("Order still not found. Showing manual activation fallback screen.");
          showScreen(ui.emailActivation);
        }
        return;
      }

      switch (error.code) {
        case "unauthenticated":
          console.error("Authentication error:", error.message);
          showActivateSubScreen();
          break;
        default:
          showActivationErrorScreen();
      }

      showActivationErrorScreen();
    });
}

function hideAllScreens() {
  Object.values(ui).forEach(el => el?.classList.add('hidden'));
}

function showScreen(screen) {
  hideAllScreens();
  screen?.classList.remove('hidden');
}

function showActivateSubScreen() {
  showScreen(ui.activateStep1);
  
  // Hide elements for embedded browsers when showing the activate screen
  if (isEmbeddedBrowser()) {
    hideElementsForEmbeddedBrowsers();
  }
}

function showActivationSuccessScreen() {
  showScreen(ui.activateStep2);
}

function showLoginErrorScreen() {
  console.log('Displaying login error screen...');
  showScreen(ui.loginError);
}

function showGeneralErrorScreen() {
  console.log('Displaying general error screen...');
  showScreen(ui.loginError);
}

function showLoginPendingScreen() {
  ui.spinner.classList.remove('hidden');
  ui.activateStep1.classList.add('hidden');
  ui.activateStep2.classList.add('hidden');
}

function showActivationErrorScreen() {
  console.log('Displaying activation error screen...');
  showScreen(ui.activationError);
}

function showActivationRetryScreen() {
    showScreen(ui.activationRetry);
}

async function handleSocialLogin(provider) {
  showLoginPendingScreen();
  try {
    const result = await signInWithPopup(auth, provider);
    result?.user ? activateUserAccount(result.user) : showLoginErrorScreen();
  } catch (err) {
    console.error(`${provider.providerId} login error:`, err.message);
    showLoginErrorScreen();
  }
}

// Login Event Bindings
const googleLoginBtn = document.getElementById('google-login');
const appleLoginBtn = document.getElementById('apple-login');

if (googleLoginBtn) {
  googleLoginBtn.addEventListener('click', () => handleSocialLogin(new GoogleAuthProvider()));
}

if (appleLoginBtn) {
  appleLoginBtn.addEventListener('click', () => handleSocialLogin(new OAuthProvider('apple.com')));
}

//document.getElementById('facebook-login').addEventListener('click', () => handleSocialLogin(new FacebookAuthProvider()));

const emailForm = document.getElementById('email-password-form');
emailForm.addEventListener('submit', async (e) => {
  e.preventDefault();
  const email = document.getElementById('email').value;
  const password = document.getElementById('password').value;

  showLoginPendingScreen();
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    userCredential?.user ? activateUserAccount(userCredential.user) : showLoginErrorScreen();
  } catch (error) {
    if (error.code === 'auth/email-already-in-use') {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        userCredential?.user ? activateUserAccount(userCredential.user) : showLoginErrorScreen();
      } catch (loginError) {
        console.error('Error logging in:', loginError.message);
        if (loginError.code === 'auth/wrong-password' || loginError.code === 'auth/user-not-found') {
          showLoginErrorScreen();
        } else {
          showGeneralErrorScreen();
        }
      }
    } else {
      showGeneralErrorScreen();
    }
  }
});

function main() {
  if (errorFound) return;

  let step = window.sessionStorage.getItem('activationStep');
  if (!step) {
    step = "1";
    window.sessionStorage.setItem('activationStep', step);
    if (oid && lid) {
      window.sessionStorage.setItem("oid", oid);
      window.sessionStorage.setItem("lid", lid);
    }
  }

  if (step === "2") {
    showActivationSuccessScreen();
  } else if (appTokenVal && activationMethod === "TOKEN") {
    validateTokenWithRetry(appTokenVal, validationUrl);
  } else {
    showActivateSubScreen();
    
    // Hide elements for embedded browsers after showing the activate screen
    if (isEmbeddedBrowser()) {
      hideElementsForEmbeddedBrowsers();
    }
  }
}

main();